import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Field } from '@mailflow/ui';
import { api } from '../lib/api.js';
import { Info } from './Info.js';

interface Sender {
  id: string;
  name: string;
  fromEmail: string;
  fromName: string;
  replyTo: string | null;
  provider: string;
  config: Record<string, unknown>;
  status: string;
  dailyLimit?: number;
  bounceLimit?: number;
  pauseReason?: string | null;
  lastVerifiedAt?: string | null;
  lastError?: string | null;
  hasCredentials: boolean;
}

interface VerifyResult {
  ok: boolean;
  message: string | null;
  lastVerifiedAt: string | null;
}

const PROVIDER_LABELS: Record<string, string> = {
  smtp: 'SMTP',
  elastic_email: 'Elastic Email',
  console: 'Konsole (nur Entwicklung)',
};

/** Beschreibt den Versandweg in einem Satz, statt nur seinen Schlüssel zu zeigen. */
function describeProvider(sender: Sender): string {
  if (sender.provider === 'console') {
    return 'Konsolenweg — schreibt statt zu senden, für Entwicklung gedacht.';
  }
  if (sender.provider === 'elastic_email') {
    return 'Elastic Email über die Schnittstelle (v4)';
  }
  const isSecure = Boolean(sender.config.secure);
  const allowSelf = Boolean(sender.config.allowSelfSigned);
  const sslText = isSecure ? 'Direct SSL/TLS' : allowSelf ? 'Unverschlüsselt / Selbstsigniert' : 'STARTTLS (Standard)';
  return `SMTP über ${String(sender.config.host ?? '—')}:${String(sender.config.port ?? 587)} (${sslText})`;
}

export function SenderList() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editingSender, setEditingSender] = useState<Sender | null>(null);

  const senders = useQuery({
    queryKey: ['senders'],
    queryFn: ({ signal }) => api.get<{ items: Sender[] }>('/api/senders', signal),
  });

  const update = useMutation({
    mutationFn: (input: {
      id: string;
      status?: string;
      dailyLimit?: number;
      bounceLimit?: number;
    }) =>
      api.patch<{ sender: Sender }>(`/api/senders/${input.id}`, {
        ...(input.status ? { status: input.status } : {}),
        ...(input.dailyLimit !== undefined ? { dailyLimit: input.dailyLimit } : {}),
        ...(input.bounceLimit !== undefined ? { bounceLimit: input.bounceLimit } : {}),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['senders'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/senders/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['senders'] }),
  });

  /*
   * Prueft die Zugangsdaten, ohne eine Mail zu verschicken. Das Ergebnis
   * landet in last_verified_at bzw. last_error — die Liste zeigt danach an,
   * wann der Weg zuletzt nachweislich funktioniert hat.
   */
  const verify = useMutation({
    mutationFn: (id: string) => api.post<VerifyResult>(`/api/senders/${id}/verify`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['senders'] }),
  });

  const items = senders.data?.items ?? [];

  return (
    <section className="card">
      <div className="card__body stack">
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h2 className="check__head" style={{ fontSize: 'var(--text-lg)' }}>
            <span>Absender</span>
            <Info zu="absenderpool" />
          </h2>
          <Button variant="secondary" onClick={() => { setEditingSender(null); setAdding(true); }}>
            + Absender hinzufügen
          </Button>
        </div>

        {items.length === 0 && !senders.isLoading && (
          <div className="alert" data-tone="warning" role="status">
            Es ist kein Absender eingerichtet. Ohne Absender verschickt der Worker nichts — die
            Nachrichten bleiben in der Warteschlange stehen, statt still zu verschwinden.
          </div>
        )}

        {items.map((sender) => (
          <article key={sender.id} className="check" data-status={statusTone(sender.status)}>
            <header className="check__head">
              <strong>
                {sender.name} — {sender.fromName} &lt;{sender.fromEmail}&gt;
              </strong>
              <span className="badge" data-check={statusTone(sender.status)}>
                {sender.status === 'active' ? 'aktiv' : 'pausiert'}
              </span>
            </header>

            <p className="muted text-sm">
              {describeProvider(sender)}
              {sender.provider !== 'console' &&
                (sender.hasCredentials
                  ? ' · Zugangsdaten hinterlegt'
                  : ' · keine Zugangsdaten hinterlegt')}
              {sender.dailyLimit
                ? ` · Tageslimit ${sender.dailyLimit.toLocaleString('de-CH')}`
                : ' · kein Tageslimit'}
              {sender.bounceLimit
                ? ` · Notbremse ab ${(sender.bounceLimit * 100).toLocaleString('de-CH')} % Bounces`
                : ' · Notbremse aus'}
            </p>

            <p className="muted text-sm">
              {sender.lastVerifiedAt
                ? `Zuletzt geprüft: ${new Date(sender.lastVerifiedAt).toLocaleString('de-CH')}`
                : 'Noch nie geprüft.'}
            </p>

            {sender.lastError && (
              <p className="text-sm" data-tone="danger">
                Letzte Prüfung fehlgeschlagen: {sender.lastError}
              </p>
            )}

            {sender.pauseReason && (
              <p className="text-sm" data-tone="warning">
                {sender.pauseReason}
              </p>
            )}

            <label className="field" style={{ maxWidth: '22rem' }}>
              <span className="field__label">
                Notbremse bei Bounce-Rate (%)
                <Info zu="notbremse" />
              </span>
              <input
                className="input input--compact"
                type="number"
                min="0"
                max="100"
                step="0.5"
                defaultValue={((sender.bounceLimit ?? 0) * 100).toString()}
                onBlur={(event) => {
                  const percent = Number(event.target.value);
                  if (!Number.isFinite(percent) || percent < 0 || percent > 100) return;
                  const next = percent / 100;
                  if (next === (sender.bounceLimit ?? 0)) return;
                  update.mutate({ id: sender.id, bounceLimit: next });
                }}
              />
              <span className="field__hint muted text-sm">
                0 schaltet die Notbremse ab. Sie greift erst ab 50 Nachrichten in 24 Stunden —
                darunter ist die Rate nicht aussagekräftig.
              </span>
            </label>

            <div className="dialog__actions">
              <Button
                variant="secondary"
                onClick={() => setEditingSender(sender)}
              >
                ✏️ Bearbeiten
              </Button>

              <Button
                variant="ghost"
                onClick={() => verify.mutate(sender.id)}
                loading={verify.isPending && verify.variables === sender.id}
              >
                🔍 Prüfen
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  update.mutate({
                    id: sender.id,
                    status: sender.status === 'active' ? 'paused' : 'active',
                  })
                }
                loading={update.isPending}
              >
                {sender.status === 'active' ? 'Pausieren' : 'Wieder aktivieren'}
              </Button>
              <Button variant="danger" onClick={() => remove.mutate(sender.id)}>
                🗑️ Entfernen
              </Button>
            </div>

            {verify.data && verify.variables === sender.id && (
              <div
                className="alert"
                data-tone={verify.data.ok ? 'success' : 'danger'}
                role="status"
              >
                {verify.data.ok
                  ? '✅ Zugangsdaten stimmen — Verbindung zum Mailserver erfolgreich aufgebaut.'
                  : (verify.data.message ?? 'Prüfung fehlgeschlagen.')}
              </div>
            )}
          </article>
        ))}

        {(adding || editingSender) && (
          <SenderDialog
            senderToEdit={editingSender}
            onClose={() => {
              setAdding(false);
              setEditingSender(null);
            }}
          />
        )}
      </div>
    </section>
  );
}

function statusTone(status: string): string {
  return status === 'active' ? 'ok' : 'warn';
}

function SenderDialog({
  senderToEdit,
  onClose,
}: {
  senderToEdit?: Sender | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEditing = Boolean(senderToEdit);

  const [name, setName] = useState(senderToEdit?.name ?? '');
  const [fromName, setFromName] = useState(senderToEdit?.fromName ?? '');
  const [fromEmail, setFromEmail] = useState(senderToEdit?.fromEmail ?? '');
  const [provider, setProvider] = useState(senderToEdit?.provider ?? 'smtp');
  
  const cfg = (senderToEdit?.config as Record<string, any>) ?? {};
  const [host, setHost] = useState(String(cfg.host ?? ''));
  const [port, setPort] = useState(String(cfg.port ?? '587'));
  const [user, setUser] = useState(String(cfg.user ?? ''));
  const [secure, setSecure] = useState<boolean>(Boolean(cfg.secure));
  const [allowSelfSigned, setAllowSelfSigned] = useState<boolean>(Boolean(cfg.allowSelfSigned));
  const [secret, setSecret] = useState('');
  const [dailyLimit, setDailyLimit] = useState(String(senderToEdit?.dailyLimit ?? '0'));

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        fromName: fromName.trim(),
        fromEmail: fromEmail.trim(),
        provider,
        ...(provider === 'smtp'
          ? {
              config: {
                host: host.trim(),
                port: Number(port) || 587,
                secure,
                allowSelfSigned,
                ...(user.trim() ? { user: user.trim() } : {}),
              },
            }
          : {}),
        ...(secret ? { secret } : {}),
        dailyLimit: Number(dailyLimit) || 0,
      };

      if (isEditing && senderToEdit) {
        return api.patch<{ sender: Sender }>(`/api/senders/${senderToEdit.id}`, payload);
      }
      return api.post<{ sender: Sender }>('/api/senders', payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['senders'] });
      onClose();
    },
  });

  const configReady =
    provider === 'console' ||
    isEditing ||
    (provider === 'elastic_email' ? secret.trim().length > 0 : host.trim().length > 0);
  const ready = name.trim() && fromName.trim() && fromEmail.trim() && configReady;

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-label={isEditing ? 'Absender bearbeiten' : 'Absender hinzufügen'}>
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>
            {isEditing ? '✏️ Absender bearbeiten' : '➕ Absender hinzufügen'}
          </h2>

          {saveMutation.isError && (
            <div className="alert" data-tone="danger" role="alert">
              {saveMutation.error instanceof Error ? saveMutation.error.message : 'Speichern fehlgeschlagen.'}
            </div>
          )}

          <Field label="Bezeichnung" value={name} onChange={(e) => setName(e.target.value)} />
          <Field
            label="Absendername"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="z. B. Muster AG"
          />
          <Field
            label="Absenderadresse"
            type="email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            placeholder="news@deine-domain.ch"
          />

          <div className="field">
            <label className="field__label" htmlFor="sender-provider">
              Versandweg
            </label>
            <select
              id="sender-provider"
              className="input"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              {Object.entries(PROVIDER_LABELS).map(([val, lbl]) => (
                <option key={val} value={val}>
                  {lbl}
                </option>
              ))}
            </select>
          </div>

          {provider === 'smtp' && (
            <div className="stack" style={{ gap: '1rem', background: 'rgba(30, 41, 59, 0.4)', padding: '1rem', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>⚙️ SMTP Mailserver Einstellungen</div>
              
              <Field label="Host / Server" value={host} onChange={(e) => setHost(e.target.value)} placeholder="z. B. smtp.deinedomain.ch oder 172.17.0.1" />
              
              <Field
                label="Port"
                type="number"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                hint="Empfohlen: 587 (STARTTLS) oder 465 (Direct SSL) oder 25 (Lokal)."
              />

              {/* VERSCHLÜSSELUNGS OPTIONS */}
              <div className="stack" style={{ gap: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={secure}
                    onChange={(e) => setSecure(e.target.checked)}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <div>
                    <strong style={{ fontSize: '0.875rem' }}>Sichere SSL/TLS-Verschlüsselung (secure: true)</strong>
                    <div className="text-xs text-muted">Aktivieren für Port 465 (Direct SSL). Für Port 587 ausschalten (nutzt STARTTLS).</div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={allowSelfSigned}
                    onChange={(e) => setAllowSelfSigned(e.target.checked)}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <div>
                    <strong style={{ fontSize: '0.875rem' }}>Unverschlüsselt / Selbstsignierte Zertifikate erlauben</strong>
                    <div className="text-xs text-muted">Erlaubt Verbindungen zum lokalen Server (Exim/Postfix) ohne SSL-Prüfung.</div>
                  </div>
                </label>
              </div>

              <Field label="Benutzername" value={user} onChange={(e) => setUser(e.target.value)} placeholder="z. B. absender@deinedomain.ch" />
              <Field
                label="Passwort"
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                hint={isEditing ? 'Leer lassen, um das gespeicherte Passwort beizubehalten.' : 'Wird verschlüsselt gespeichert.'}
              />
            </div>
          )}

          {provider === 'elastic_email' && (
            <>
              <Field
                label="API-Schlüssel"
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                hint={isEditing ? 'Leer lassen, um den bisherigen Schlüssel zu behalten.' : 'Wird verschlüsselt gespeichert.'}
              />
            </>
          )}

          <Field
            label="Tageslimit"
            type="number"
            min="0"
            value={dailyLimit}
            onChange={(e) => setDailyLimit(e.target.value)}
            hint="0 bedeutet unbegrenzt."
          />

          <div className="dialog__actions">
            <Button variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!ready} loading={saveMutation.isPending}>
              {isEditing ? 'Änderungen speichern' : 'Anlegen'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
